/**
 * Deux saisies sur une version de gamme, dans un panneau :
 *
 * - « nouvelle » : créer un brouillon COPIÉ d'une version existante (contenu et prix repris tels
 *   quels, côté serveur) avec sa date d'effet, sa source et son motif. C'est ainsi qu'on corrige une
 *   gamme publiée : une publiée ne change jamais, on en publie une nouvelle.
 * - « infos » : changer la date d'effet, la source ou le motif d'un brouillon (jamais son contenu).
 *   Le jeton de concurrence reçu avec le brouillon est renvoyé : si quelqu'un d'autre l'a modifié
 *   depuis, l'API refuse et l'écran le dit au lieu d'écraser son travail.
 */
import { useState } from 'react';

import { ApiError, api, type GammeDetail, type GammeVersion } from '../api/client';
import { dateFr } from '../domaine/gammes';
import { aujourdhuiLocal } from '../domaine/gammes-versions';
import { Champ, ErreurFormulaire, Ligne, Panneau } from './Formulaire';

const SOURCE_PAR_DEFAUT = 'Saisie à l’écran';

export function FormulaireVersion({
  mode,
  gamme,
  version,
  onFermer,
  onEnregistree,
  onConflit,
}: {
  /** `premiere` : le brouillon VIDE d'une gamme qui n'a encore aucune version (il n'y a rien à copier). */
  mode: 'nouvelle' | 'infos' | 'premiere';
  gamme: GammeDetail;
  /** `nouvelle` : la version à copier. `infos` : le brouillon à corriger. `premiere` : aucune. */
  version: GammeVersion | null;
  onFermer: () => void;
  onEnregistree: (version: GammeVersion) => void;
  /** Quelqu'un d'autre a modifié la version entre-temps : relire la gamme et fermer ce panneau. */
  onConflit: () => void;
}) {
  const creation = mode !== 'infos';

  const [validFrom, setValidFrom] = useState(creation || !version ? aujourdhuiLocal() : version.validFrom);
  const [source, setSource] = useState(creation || !version ? SOURCE_PAR_DEFAUT : version.source);
  const [motif, setMotif] = useState(creation || !version ? '' : version.motif);
  const [erreur, setErreur] = useState<ApiError | Error | null>(null);
  const [enCours, setEnCours] = useState(false);

  const enregistrer = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(validFrom)) {
      setErreur(new Error('Indiquez la date d’effet.'));
      return;
    }
    if (source.trim() === '') {
      setErreur(new Error('La source est obligatoire : d’où viennent ces informations ?'));
      return;
    }
    if (motif.trim() === '') {
      setErreur(new Error('Le motif est obligatoire : pourquoi cette version ?'));
      return;
    }

    setEnCours(true);
    setErreur(null);
    try {
      if (creation) {
        onEnregistree(
          await api.createMaintenanceVersion(gamme.id, {
            validFrom,
            source: source.trim(),
            motif: motif.trim(),
            ...(version ? { fromVersionId: version.id } : {}),
          }),
        );
        return;
      }
      if (!version) return;

      // Seulement ce qui a changé : ne pas écrire (ni tracer) un enregistrement qui ne change rien.
      const changements = {
        ...(validFrom === version.validFrom ? {} : { validFrom }),
        ...(source.trim() === version.source ? {} : { source: source.trim() }),
        ...(motif.trim() === version.motif ? {} : { motif: motif.trim() }),
      };
      if (Object.keys(changements).length === 0) {
        onFermer();
        return;
      }
      onEnregistree(
        await api.updateMaintenanceVersion(gamme.id, version.id, { ...changements, revision: version.revision }),
      );
    } catch (e) {
      setErreur(e instanceof ApiError || e instanceof Error ? e : new Error('Enregistrement impossible.'));
    } finally {
      setEnCours(false);
    }
  };

  return (
    <Panneau
      titre={
        mode === 'premiere'
          ? `Première version de ${gamme.code}`
          : creation
            ? `Nouvelle version de ${gamme.code}`
            : `Informations de la version ${version?.versionNumber ?? ''}`
      }
      sousTitre={
        mode === 'premiere'
          ? 'Un brouillon vide : ses paliers et ses pièces se composent ensuite avec « Modifier le contenu ». Rien ne s’applique aux machines tant qu’il n’est pas publié.'
          : creation
            ? `Copiée de la version ${version?.versionNumber ?? ''} (contenu et prix repris tels quels) : une version publiée ne change jamais, on en publie une nouvelle.`
            : 'Date d’effet, source et motif du brouillon. Son contenu se modifie avec « Modifier le contenu ».'
      }
      onFermer={onFermer}
      actions={
        <>
          <button className="secondaire" onClick={onFermer} disabled={enCours}>
            Annuler
          </button>
          <button onClick={() => void enregistrer()} disabled={enCours}>
            {enCours ? 'Enregistrement…' : creation ? 'Créer le brouillon' : 'Enregistrer'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void enregistrer();
        }}
      >
        <ErreurFormulaire
          message={erreur?.message ?? null}
          details={erreur instanceof ApiError ? erreur.details : undefined}
        />
        {/* 409 : la version a changé sous nos yeux. Il faut la relire avant de pouvoir la corriger. */}
        {!creation && erreur instanceof ApiError && erreur.status === 409 && (
          <p>
            <button type="button" className="secondaire" onClick={onConflit}>
              Recharger la version
            </button>
          </p>
        )}

        <Ligne>
          <Champ
            libelle="Date d’effet"
            requis
            aide={
              creation
                ? 'À partir de quand cette version s’applique aux machines. Aujourd’hui par défaut.'
                : `Actuellement ${version ? dateFr(version.validFrom) : '—'}.`
            }
          >
            <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
          </Champ>
        </Ligne>

        <Champ libelle="Motif" requis aide="Pourquoi cette version : « Prix du tarif 2027 », « Correction d’une quantité »…">
          <textarea
            rows={3}
            autoFocus
            placeholder="Pourquoi cette version ?"
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
          />
        </Champ>

        <Champ libelle="Source" requis aide="D’où viennent les informations : un export, un tarif, une saisie.">
          <input type="text" value={source} onChange={(e) => setSource(e.target.value)} />
        </Champ>
      </form>
    </Panneau>
  );
}
